import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { User, Edit2, Check, X, Crown, Zap, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '@/components/Footer';
import { logout } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_LENSES = [
  { id: 'jungian', name: 'Jungian Psychology' },
  { id: 'buddhism', name: 'Buddhism' },
  { id: 'taoism', name: 'Taoism' },
  { id: 'christianity', name: 'Christianity' },
  { id: 'hinduism', name: 'Hinduism' },
  { id: 'sufism', name: 'Sufism/Mysticism' },
  { id: 'greek-myth', name: 'Greek Mythology' },
  { id: 'norse-myth', name: 'Norse Mythology' },
  { id: 'fairy-tales', name: 'Fairy Tale Archetypes' },
  { id: 'fantasy-lit', name: 'Fantasy Literature' },
  { id: 'stoicism', name: 'Stoicism' },
  { id: 'science', name: 'Biology/Science' }
];

const TONE_OPTIONS = [
  { id: 'mystical', name: 'Mystical' },
  { id: 'grounded', name: 'Grounded' },
  { id: 'scholarly', name: 'Scholarly' },
  { id: 'poetic', name: 'Poetic' }
];

const STRUCTURE_OPTIONS = [
  { id: 'multi-lens', name: 'Multi-Lens' },
  { id: 'unified', name: 'Unified' }
];

export default function Profile({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selectedLenses, setSelectedLenses] = useState([]);
  const [tone, setTone] = useState('mystical');
  const [structure, setStructure] = useState('multi-lens');
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    loadProfile();
    loadSubscriptionStatus();

    // Check for subscription success/cancel from URL params
    const subscriptionParam = searchParams.get('subscription');
    if (subscriptionParam === 'success') {
      toast.success('🎉 Welcome to Pro! You now have unlimited access.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (subscriptionParam === 'cancelled') {
      toast.info('Subscription cancelled. You can upgrade anytime!');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  const loadProfile = async () => {
    try {
      const resp = await axios.get(`${API}/profile`);
      setProfile(resp.data);
      if (resp.data) {
        setSelectedLenses(resp.data.selected_lenses);
        setTone(resp.data.tone_preference);
        setStructure(resp.data.reading_structure);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const resp = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(resp.data);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
    }
  };

  const toggleLens = (lensName) => {
    if (selectedLenses.includes(lensName)) {
      setSelectedLenses(selectedLenses.filter(l => l !== lensName));
    } else {
      const maxLenses = subscriptionStatus?.lenses_limit || 6;
      if (selectedLenses.length >= maxLenses) {
        if (subscriptionStatus?.tier === 'free') {
          toast.error(`Free tier allows ${maxLenses} lenses. Upgrade to Pro for 6 lenses!`);
        } else {
          toast.error(`Maximum ${maxLenses} lenses`);
        }
        return;
      }
      setSelectedLenses([...selectedLenses, lensName]);
    }
  };

  const handleSave = async () => {
    if (selectedLenses.length < 2) {
      toast.error('Select at least 2 lenses');
      return;
    }

    try {
      await axios.put(`${API}/profile/update`, {
        selected_lenses: selectedLenses,
        tone_preference: tone,
        reading_structure: structure
      });
      toast.success('Profile updated');
      setEditing(false);
      loadProfile();
      loadSubscriptionStatus();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response.data.detail || 'Upgrade to Pro for more lenses');
      } else {
        toast.error('Failed to update');
      }
      console.error(err);
    }
  };

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      const resp = await axios.post(`${API}/subscription/create-checkout`, { plan });
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      toast.error('Failed to start checkout');
      console.error(err);
      setUpgrading(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setSelectedLenses(profile.selected_lenses);
      setTone(profile.tone_preference);
      setStructure(profile.reading_structure);
    }
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <div className="cosmic-loader"></div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24">
      <div className="fade-in max-w-[1000px] mx-auto pb-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <User size={48} className="text-gold shrink-0" />
            <div className="min-w-0">
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase m-0 truncate">Your Profile</h1>
              <p className="text-gray-400 m-0 truncate">{user?.email}</p>
            </div>
          </div>

          <div className="flex gap-3 w-full sm:w-auto">
            {!editing ? (
              <>
                <Button
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-button"
                  className="btn-outline-gold flex-1 sm:flex-none py-2"
                >
                  <Edit2 size={18} className="mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => logout(navigate)}
                  data-testid="logout-button"
                  className="btn-outline-gold flex-1 sm:flex-none py-2"
                >
                  <LogOut size={18} className="mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleCancel}
                  data-testid="cancel-edit-button"
                  className="btn-outline-gold flex-1 sm:flex-none py-2"
                >
                  <X size={18} className="mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  data-testid="save-profile-button"
                  className="btn-gold flex-1 sm:flex-none py-2"
                >
                  <Check size={18} className="mr-2" />
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Subscription Status */}
        {subscriptionStatus && (
          <div className={`mystical-card p-6 md:p-8 mb-8 transition-all ${subscriptionStatus.tier === 'pro'
            ? 'bg-gradient-to-br from-gold/10 to-gold/5 border-gold/40'
            : ''
            }`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8 pb-6 border-b border-gold/10">
              <div className="flex items-center gap-3">
                {subscriptionStatus.tier === 'pro' ? (
                  <Crown size={32} className="text-gold" />
                ) : (
                  <Zap size={32} className="text-gold" />
                )}
                <div>
                  <h2 className="text-2xl font-bold uppercase tracking-widest m-0">
                    {subscriptionStatus.tier === 'pro' ? 'Pro Tier' : 'Free Tier'}
                  </h2>
                  <p className="text-xs text-gray-400 uppercase tracking-tighter mt-1">Current Membership</p>
                </div>
              </div>
              {subscriptionStatus.tier === 'free' && (
                <Button
                  onClick={() => handleUpgrade('monthly')}
                  disabled={upgrading}
                  className="btn-gold w-full sm:w-auto px-6 text-sm"
                >
                  <Crown size={16} className="mr-2" />
                  Upgrade to Pro
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="text-center sm:text-left">
                <p className="text-gray-400 uppercase tracking-widest text-xs mb-1">Monthly Readings</p>
                <p className="text-3xl font-bold text-gold m-0">
                  {subscriptionStatus.tier === 'pro' ? 'Unlimited' : `${subscriptionStatus.readings_used}/${subscriptionStatus.readings_limit}`}
                </p>
              </div>
              <div className="text-center sm:text-left">
                <p className="text-gray-400 uppercase tracking-widest text-xs mb-1">Wisdom Lenses</p>
                <p className="text-3xl font-bold text-gold m-0">
                  {subscriptionStatus.lenses_limit} Available
                </p>
              </div>
            </div>

            {subscriptionStatus.tier === 'free' && (
              <div className="mt-8 p-6 bg-gold/5 rounded-xl border border-gold/20">
                <h3 className="text-lg font-bold uppercase tracking-widest mb-4">Pro Benefits</h3>
                <ul className="space-y-2 mb-6 text-gray-400 text-sm list-disc pl-5">
                  <li>Unlimited devotional readings</li>
                  <li>Access to all 6 wisdom lenses</li>
                  <li>Priority support</li>
                </ul>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => handleUpgrade('monthly')}
                    disabled={upgrading}
                    className="btn-outline-gold flex-1"
                  >
                    $4.99 / Month
                  </Button>
                  {/* <Button
                    onClick={() => handleUpgrade('yearly')}
                    disabled={upgrading}
                    className="btn-gold flex-1"
                  >
                    $39 / Year <span className="text-xs ml-2 opacity-80">(Save 35%)</span>
                  </Button> */}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lenses */}
        <div className="mystical-card p-6 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-bold uppercase tracking-widest mb-6 border-b border-gold/20 pb-4">
            Wisdom Lenses <span className="text-gold ml-2">({selectedLenses.length}/{subscriptionStatus?.lenses_limit || 6})</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {AVAILABLE_LENSES.map(lens => {
              const isSelected = selectedLenses.includes(lens.name);
              return (
                <div
                  key={lens.id}
                  onClick={() => editing && toggleLens(lens.name)}
                  className={`lens-card text-gold p-4 text-center text-sm font-bold uppercase tracking-tighter transition-all duration-300 ${isSelected ? 'selected opacity-100' : 'text-gold opacity-60'
                    } ${editing ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {lens.name}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tone & Structure */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="mystical-card p-6 md:p-8">
            <h2 className="text-xl font-bold uppercase tracking-widest mb-6 border-b border-gold/20 pb-4">Reading Tone</h2>
            <div className="grid grid-cols-2 gap-4">
              {TONE_OPTIONS.map(t => (
                <div
                  key={t.id}
                  onClick={() => editing && setTone(t.id)}
                  className={`lens-card text-gold p-4 text-center text-sm font-bold uppercase tracking-tighter transition-all duration-300 ${tone === t.id ? 'selected opacity-100' : 'text-gold opacity-60'
                    } ${editing ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {t.name}
                </div>
              ))}
            </div>
          </div>

          <div className="mystical-card p-6 md:p-8">
            <h2 className="text-xl font-bold uppercase tracking-widest mb-6 border-b border-gold/20 pb-4">Structure</h2>
            <div className="grid grid-cols-2 gap-4">
              {STRUCTURE_OPTIONS.map(s => (
                <div
                  key={s.id}
                  onClick={() => editing && setStructure(s.id)}
                  className={`lens-card text-gold p-4 text-center text-sm font-bold uppercase tracking-tighter transition-all duration-300 ${structure === s.id ? 'selected opacity-100' : 'text-gold opacity-60'
                    } ${editing ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
